import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { useEntity } from '../../context/EntityContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Building2,
  Users,
  Award,
  Calendar,
  FileText,
  DollarSign,
  Banknote,
  UserCheck,
  AlertTriangle,
  TrendingUp,
  BarChart3,
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentEntity } = useEntity();

  useEffect(() => {
    loadStats();
  }, [currentEntity]);

  const loadStats = async () => {
    try {
      const entityParam = currentEntity !== 'all' ? `?entity=${currentEntity}` : '';
      const res = await dashboardAPI.getStats(entityParam);
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color = "primary" }) => {
    const colorStyles = {
      primary: "bg-[#4E50FF]/10 text-[#4E50FF]",
      success: "bg-green-100 text-green-600",
      warning: "bg-amber-100 text-amber-600",
      info: "bg-blue-100 text-blue-600",
      orange: "bg-orange-100 text-orange-600",
    };

    return (
      <Card className="border shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${colorStyles[color]}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{value}</p>
              <p className="text-sm text-slate-500">{title}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-[#4E50FF]" />
            Admin Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Overview of your platform</p>
        </div>
        {currentEntity !== 'all' && (
          <Badge className={currentEntity === 'sam_rumah' ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'}>
            Filtered: {currentEntity === 'sam_rumah' ? 'SAM Rumah' : 'SP SAM Property'}
          </Badge>
        )}
      </div>

      {/* Entity Breakdown */}
      {stats?.entity_breakdown && currentEntity === 'all' && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">SR</div>
              <div>
                <p className="text-lg font-bold text-emerald-800">{stats.entity_breakdown.sam_rumah || 0}</p>
                <p className="text-xs text-emerald-600">SAM Rumah Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm">SP</div>
              <div>
                <p className="text-lg font-bold text-blue-800">{stats.entity_breakdown.sp_sam || 0}</p>
                <p className="text-xs text-blue-600">SP SAM Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500 flex items-center justify-center text-white font-bold text-sm">?</div>
              <div>
                <p className="text-lg font-bold text-gray-800">{stats.entity_breakdown.untagged || 0}</p>
                <p className="text-xs text-gray-600">Untagged Bookings</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Card */}
      <Card className="bg-gradient-to-r from-[#4E50FF] to-[#7B7DFF] text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-xl bg-white/20">
              <TrendingUp className="w-10 h-10 text-white" />
            </div>
            <div>
              <p className="text-sm text-white/80">Total Revenue</p>
              <p className="text-4xl font-bold font-mono">
                {formatCurrency(stats?.total_revenue || 0)}
              </p>
              <p className="text-xs text-white/60 mt-1">From completed sales</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.total_users || 0}
          icon={Users}
          color="primary"
        />
        <StatCard
          title="Total Agents"
          value={stats?.total_agents || 0}
          icon={Award}
          color="success"
        />
        <StatCard
          title="Properties"
          value={stats?.total_properties || 0}
          icon={Building2}
          color="info"
        />
        <StatCard
          title="Bookings"
          value={stats?.total_bookings || 0}
          icon={FileText}
          color="warning"
        />
      </div>

      {/* Pending Actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Pending Actions</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <Card className={`border ${stats?.pending_kyc > 0 ? 'border-amber-300 bg-amber-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <UserCheck className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pending_kyc || 0}</p>
                  <p className="text-xs text-slate-500">Pending KYC</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border ${stats?.pending_claims > 0 ? 'border-green-300 bg-green-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pending_claims || 0}</p>
                  <p className="text-xs text-slate-500">Pending Claims</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border ${stats?.pending_withdrawals > 0 ? 'border-blue-300 bg-blue-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Banknote className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pending_withdrawals || 0}</p>
                  <p className="text-xs text-slate-500">Pending Withdrawals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border ${stats?.escalated_appointments > 0 ? 'border-orange-300 bg-orange-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.escalated_appointments || 0}</p>
                  <p className="text-xs text-slate-500">Escalated Appointments</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
